import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAuthStore } from './auth';

interface UserToken {
  id: string;
  userId: string;
  name: string;
  tokenPrefix: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

interface UserTokenCreate {
  name: string;
  expiresInDays?: number | null;
}

interface TokenCreatedResponse {
  token: UserToken;
  rawToken: string;
}

export const useTokensStore = defineStore('tokens', () => {
  const tokens = ref<UserToken[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const newlyCreatedToken = ref<string | null>(null);

  const authStore = useAuthStore();

  async function fetchTokens() {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch('/api/tokens', {
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tokens');
      }

      const data = await response.json();
      tokens.value = data.tokens;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
    } finally {
      loading.value = false;
    }
  }

  async function createToken(data: UserTokenCreate): Promise<TokenCreatedResponse | null> {
    loading.value = true;
    error.value = null;
    newlyCreatedToken.value = null;

    try {
      const response = await fetch('/api/tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authStore.token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to create token');
      }

      const result: TokenCreatedResponse = await response.json();
      tokens.value.unshift(result.token);
      newlyCreatedToken.value = result.rawToken;
      return result;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function revokeToken(tokenId: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/tokens/${tokenId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to revoke token');
      }

      // Update the token in the list
      const index = tokens.value.findIndex((t) => t.id === tokenId);
      if (index !== -1) {
        tokens.value[index].revokedAt = new Date().toISOString();
      }
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return false;
    } finally {
      loading.value = false;
    }
  }

  function clearNewToken() {
    newlyCreatedToken.value = null;
  }

  return {
    tokens,
    loading,
    error,
    newlyCreatedToken,
    fetchTokens,
    createToken,
    revokeToken,
    clearNewToken,
  };
});
